/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/blood.tsx
   Blood domain cards — updated to Void 1.5.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  STRESS,
  HOPE,
  FEAR,
  VERY_CLOSE,
  CLOSE,
  MELEE,
  FAR,
  VERY_FAR,
  HIT_POINT,
  VULNERABLE,
  ADVANTAGE,
  DISADVANTAGE,
} from '../../templates';

const bloodCards: DomainCard[] = [

  // ── Level 1 ────────────────────────────────────────────
  {
    name: 'Blood Spike',
    level: 1,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Far range. On a success, mark a Stress to deal d10 magic damage to the target using your Proficiency. On a success with Hope, the target also marks a Stress. On a roll with Fear, mark a Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {FAR} range. On a success, mark a {STRESS} to deal <strong className="inline-strong">d10</strong> magic damage to the target using your Proficiency. On a success with Hope, the target also marks a {STRESS}. On a roll with Fear, mark a {STRESS}.</p>
      </>
    ),
  },
  {
    name: 'Lifeblood Talisman',
    level: 1,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 0,
    text: "Mark a Hit Point to conjure a talisman infused with your life essence. The talisman appears in your hand, and whoever carries the talisman gains its benefit: Whenever the talisman's bearer marks 2 or more Hit Points, they can spend a Hope to reduce the number of Hit Points marked by 1. The talisman disappears if you have no Hit Points marked or you use this spell again.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Mark a {HIT_POINT} to conjure a talisman infused with your life essence. The talisman appears in your hand, and whoever carries the talisman gains its benefit: Whenever the talisman's bearer marks 2 or more {HIT_POINT}s, they can spend a {HOPE} to reduce the number of {HIT_POINT}s marked by 1. The talisman disappears if you have no {HIT_POINT}s marked or you use this spell again.</p>
      </>
    ),
  },
  {
    name: 'Power Through Pain',
    level: 1,
    domain: 'Blood',
    type: 'Ability',
    recallCost: 1,
    text: "If you have at least one Hit Point marked, you gain a bonus to your damage rolls. The bonus equals twice your number of marked Hit Points.",
    rendered: (
      <>
        <p key={0} className="card-text-para">If you have at least one {HIT_POINT} marked, you gain a bonus to your damage rolls. The bonus equals twice your number of marked {HIT_POINT}s.</p>
      </>
    ),
  },

  // ── Level 2 ────────────────────────────────────────────
  {
    name: 'Brand of Castigation',
    level: 2,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "When you deal damage to a creature, mark a Stress to sear a red, magical mark on them. Until this mark disappears, you always know the direction of the marked creature relative to you, and that creature marks a Stress each time it deals damage to you or an ally of yours within Very Close range of you. The mark disappears when you use this spell again.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you deal damage to a creature, mark a {STRESS} to sear a red, magical mark on them. Until this mark disappears, you always know the direction of the marked creature relative to you, and that creature marks a {STRESS} each time it deals damage to you or an ally of yours within {VERY_CLOSE} range of you. The mark disappears when you use this spell again.</p>
      </>
    ),
  },
  {
    name: 'Vitality Manipulation',
    level: 2,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 0,
    text: "Make a Spellcast Roll against a target within Very Close range. If you cast this on an ally, make the roll with advantage. On a success, mark a Stress, and choose one of the following effects: The target grows calmer and clears a Stress. On a success with Hope, they clear 2 Stress. The target grows more anxious and marks a Stress. On a success with Hope, they mark 2 Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {VERY_CLOSE} range. If you cast this on an ally, make the roll with {ADVANTAGE}. On a success, mark a {STRESS}, and choose one of the following effects:</p>
        <ul key={1}>
          <li>The target grows calmer and clears a {STRESS}. On a success with Hope, they clear 2 {STRESS}.</li>
          <li>The target grows more anxious and marks a {STRESS}. On a success with Hope, they mark 2 {STRESS}.</li>
        </ul>
      </>
    ),
  },

  // ── Level 3 ────────────────────────────────────────────
  {
    name: 'Burning Blood',
    level: 3,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll (12). On a success, mark a Hit Point as you conjure a bead of blood, which you lob at a point within Far range. A wave of heat fills the area within Very Close range of that point, igniting the internal vitals of those caught within. Each target within the area marks 1 Hit Point. On a success with Hope, each target within the area instead marks 2 Hit Points.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll (12)</strong>. On a success, mark a {HIT_POINT} as you conjure a bead of blood, which you lob at a point within {FAR} range. A wave of heat fills the area within {VERY_CLOSE} range of that point, igniting the internal vitals of those caught within. Each target within the area marks 1 {HIT_POINT}. On a success with Hope, each target within the area instead marks 2 {HIT_POINT}s.</p>
      </>
    ),
  },
  {
    name: 'Blood Puppet',
    level: 3,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll against a creature (living or dead) within Far range. On a success, spend a Hope to control the target by causing them to move, attack, or both. If you do both, you choose the order. If you cause the creature to move, they move to a location you choose that's within Close range of them. If you cause the creature to attack, make a Spellcast Roll against a target within Melee range of them. On a success, deal d10 physical damage using your Proficiency.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a creature (living or dead) within {FAR} range. On a success, spend a {HOPE} to control the target by causing them to move, attack, or both. If you do both, you choose the order.</p>
        <p key={1} className="card-text-para">If you cause the creature to move, they move to a location you choose that's within {CLOSE} range of them. If you cause the creature to attack, make a Spellcast Roll against a target within {MELEE} range of them. On a success, deal <strong className="inline-strong">d10</strong> physical damage using your Proficiency.</p>
      </>
    ),
  },

  // ── Level 4 ────────────────────────────────────────────
  {
    name: 'Grisly Harpoon',
    level: 4,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "You launch a harpoon of blood at a location or creature within Far range. If the target is a location, make a Spellcast Roll (13). On a success, mark a Stress to pull yourself to a position within Melee range of it. If the target is a creature, make a Spellcast Roll against it. On a success, mark a Stress to deal 3d8 magic damage to the target. You then pull the target straight toward yourself or pull yourself straight toward the target, ending within Melee range of each other.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You launch a harpoon of blood at a location or creature within {FAR} range. If the target is a location, make a <strong className="inline-strong">Spellcast Roll (13)</strong>. On a success, mark a {STRESS} to pull yourself to a position within {MELEE} range of it.</p>
        <p key={1} className="card-text-para">If the target is a creature, make a <strong className="inline-strong">Spellcast Roll</strong> against it. On a success, mark a {STRESS} to deal <strong className="inline-strong">3d8</strong> magic damage to the target. You then pull the target straight toward yourself or pull yourself straight toward the target, ending within {MELEE} range of each other.</p>
      </>
    ),
  },
  {
    name: 'Weave the Flesh',
    level: 4,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "Once per rest, mark a Hit Point to allow each ally within Close range to clear a Hit Point or a Stress. You can mark a Stress to allow those allies to clear one of each.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, mark a {HIT_POINT} to allow each ally within {CLOSE} range to clear a {HIT_POINT} or a {STRESS}. You can mark a {STRESS} to allow those allies to clear one of each.</p>
      </>
    ),
  },

  // ── Level 5 ────────────────────────────────────────────
  {
    name: 'Mutual Suffering',
    level: 5,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "When an attack from a creature causes you to mark one or more Hit Points, you can make a Reaction Roll using your Spellcast trait against the creature. On a success, the creature marks the same number of Hit Points as you did, and you can't use this spell again until you finish a rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an attack from a creature causes you to mark one or more {HIT_POINT}s, you can make a <strong className="inline-strong">Reaction Roll</strong> using your Spellcast trait against the creature. On a success, the creature marks the same number of {HIT_POINT}s as you did, and you can't use this spell again until you finish a rest.</p>
      </>
    ),
  },
  {
    name: 'Parasite of the Will',
    level: 5,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "Make a Spellcast Roll against a creature within Very Far range. On a success, mark a Hit Point to conjure a tiny magical bloodworm that burrows into the target. On a success with Hope, the target isn't aware of the worm within. You have advantage on any Presence roll against the target, and whenever they make a roll, you can spend a Hope to give the roll disadvantage. You can destroy the bloodworm to cause the target to mark a Hit Point.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a creature within {VERY_FAR} range. On a success, mark a {HIT_POINT} to conjure a tiny magical bloodworm that burrows into the target. On a success with Hope, the target isn't aware of the worm within.</p>
        <p key={1} className="card-text-para">You have {ADVANTAGE} on any Presence roll against the target, and whenever they make a roll, you can spend a {HOPE} to give the roll {DISADVANTAGE}. You can destroy the bloodworm to cause the target to mark a {HIT_POINT}.</p>
      </>
    ),
  },

  // ── Level 6 ────────────────────────────────────────────
  {
    name: 'Vital Ward',
    level: 6,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "Mark a Hit Point to trace a circle of blood around yourself at Very Close range. While in the circle, you have resistance to your choice of physical or magic damage (choose when you cast the spell). Allies also gain this benefit while in the circle. The circle disappears if you move out of it, mark 2 or more Hit Points, or cast this spell again.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Mark a {HIT_POINT} to trace a circle of blood around yourself at {VERY_CLOSE} range. While in the circle, you have resistance to your choice of physical or magic damage (choose when you cast the spell). Allies also gain this benefit while in the circle. The circle disappears if you move out of it, mark 2 or more {HIT_POINT}s, or cast this spell again.</p>
      </>
    ),
  },
  {
    name: 'Blood Bind',
    level: 6,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll against a target within Far range. On a success, mark a Stress as you slow the target's vitality. The target is temporarily Restrained and temporarily Vulnerable. Each time the target is spotlighted while either of these conditions persists on them, the target takes d10 magic damage using your Proficiency. The spell ends early on the target if you use it again.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a target within {FAR} range. On a success, mark a {STRESS} as you slow the target's vitality. The target is temporarily <strong className="inline-strong">Restrained</strong> and temporarily {VULNERABLE}. Each time the target is spotlighted while either of these conditions persists on them, the target takes <strong className="inline-strong">d10</strong> magic damage using your Proficiency. The spell ends early on the target if you use it again.</p>
      </>
    ),
  },

  // ── Level 7 ────────────────────────────────────────────
  {
    name: 'Blood-Touched',
    level: 7,
    domain: 'Blood',
    type: 'Ability',
    recallCost: 1,
    text: "While 4 or more of the domain cards in your loadout are from the Blood domain, gain the following benefits: When you take enough damage to mark 2 or more Hit Points, gain a Hope. For every 3 Hit Points you have marked, gain a +1 bonus to your Evasion.",
    rendered: (
      <>
        <p key={0} className="card-text-para">While 4 or more of the domain cards in your loadout are from the Blood domain, gain the following benefits:</p>
        <ul key={1}>
          <li>When you take enough damage to mark 2 or more {HIT_POINT}s, gain a {HOPE}.</li>
          <li>For every 3 {HIT_POINT}s you have marked, gain a +1 bonus to your Evasion.</li>
        </ul>
      </>
    ),
  },
  {
    name: 'Vampiric Strike',
    level: 7,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "When you make a successful attack roll against an adversary and cause them to mark 2 or more Hit Points, you can spend a Hope to clear a Hit Point or Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you make a successful attack roll against an adversary and cause them to mark 2 or more {HIT_POINT}s, you can spend a {HOPE} to clear a {HIT_POINT} or {STRESS}.</p>
      </>
    ),
  },

  // ── Level 8 ────────────────────────────────────────────
  {
    name: 'Life Leash',
    level: 8,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "Spend a Hope to allow yourself and a willing ally within Far range to redistribute marked Hit Points between the two of you. You then can't target that ally again with Life Leash until you finish a rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Spend a {HOPE} to allow yourself and a willing ally within {FAR} range to redistribute marked {HIT_POINT}s between the two of you. You then can't target that ally again with Life Leash until you finish a rest.</p>
      </>
    ),
  },
  {
    name: 'Runic Adrenaline',
    level: 8,
    domain: 'Blood',
    type: 'Ability',
    recallCost: 1,
    text: "Your practice of blood magic has seeped into your bloodstream, enhancing your vitals in moments of urgency. When you roll with advantage, use a d8 instead of a d6 as your advantage die if you have 1 or more Hit Points marked. After you make a Strength, an Agility, or a Finesse roll, you can mark a Hit Point to roll 1d8 and add it to the result.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Your practice of blood magic has seeped into your bloodstream, enhancing your vitals in moments of urgency. When you roll with {ADVANTAGE}, use a d8 instead of a d6 as your advantage die if you have 1 or more {HIT_POINT}s marked.</p>
        <p key={1} className="card-text-para">After you make a Strength, an Agility, or a Finesse roll, you can mark a {HIT_POINT} to roll <strong className="inline-strong">1d8</strong> and add it to the result.</p>
      </>
    ),
  },

  // ── Level 9 ────────────────────────────────────────────
  {
    name: 'Bloodbath',
    level: 9,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "Once per rest, spend a Hope to unleash waves of blood around yourself. Make a single Spellcast Roll against each adversary within Close range. On a success, a target marks a Hit Point and a Stress. On a failure, a target marks a Stress. Each ally within Close range marks a Stress but clears a Hit Point.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, spend a {HOPE} to unleash waves of blood around yourself. Make a single <strong className="inline-strong">Spellcast Roll</strong> against each adversary within {CLOSE} range. On a success, a target marks a {HIT_POINT} and a {STRESS}. On a failure, a target marks a {STRESS}. Each ally within {CLOSE} range marks a {STRESS} but clears a {HIT_POINT}.</p>
      </>
    ),
  },
  {
    name: 'Glyph of Hemorrhaging',
    level: 9,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 1,
    text: "Make a Spellcast Roll against a creature within Far range. On a success, mark a Hit Point to sear the target with a magical glyph that lasts until the GM spends 2 Fear to remove it or you take Severe damage. Whenever the target marks any Hit Points while the glyph remains, you can mark a Stress to make them mark an additional Hit Point.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against a creature within {FAR} range. On a success, mark a {HIT_POINT} to sear the target with a magical glyph that lasts until the GM spends 2 {FEAR} to remove it or you take Severe damage. Whenever the target marks any {HIT_POINT}s while the glyph remains, you can mark a {STRESS} to make them mark an additional {HIT_POINT}.</p>
      </>
    ),
  },

  // ── Level 10 ───────────────────────────────────────────
  {
    name: 'Crimson Adamance',
    level: 10,
    domain: 'Blood',
    type: 'Ability',
    recallCost: 1,
    text: "When you would mark your last Hit Point, spend a Hope to mark a Stress instead.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you would mark your last {HIT_POINT}, spend a {HOPE} to mark a {STRESS} instead.</p>
      </>
    ),
  },
  {
    name: 'Sanguine Feast',
    level: 10,
    domain: 'Blood',
    type: 'Spell',
    recallCost: 2,
    text: "Make a Spellcast Roll against an adversary within Close range. On a success, spend 2 Hope to mark 1–3 Hit Points, and the target marks twice the number of the Hit Points you marked. If this causes the target to mark their last Hit Point, you can clear the Hit Points you marked to cast this spell.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a <strong className="inline-strong">Spellcast Roll</strong> against an adversary within {CLOSE} range. On a success, spend 2 {HOPE} to mark 1–3 {HIT_POINT}s, and the target marks twice the number of the {HIT_POINT}s you marked. If this causes the target to mark their last {HIT_POINT}, you can clear the {HIT_POINT}s you marked to cast this spell.</p>
      </>
    ),
  },

];

export default bloodCards;
